import React, { useState } from 'react'
import classNames from 'classnames'
import { IconX } from '@tabler/icons-react'
import { isFunction, isString } from 'lodash'

import { Button } from '@/components/buttons/Button'

import { Modal } from './Modal'

/**
 * @import import { BasicModal } from '@/components/layout/modals/BasicModal'
 * @param {object} props
 * @param {Function} [props.trigger] Render prop `(open) => node` that opens a self-managed modal; without it, visibility is controlled via show
 * @param {node} [props.title] Heading in the modal header; when a string it also becomes the dialog aria-label
 * @param {Function} [props.onClose] Called when the modal is dismissed
 * @param {node|Function} props.children Modal body; with a trigger, a function receives the close handler
 * @param {node} [props.footer] Actions rendered at the bottom of the modal
 * @param {boolean} [props.show] Controlled visibility when there is no trigger
 * @param {boolean} [props.showCloseButton] Set false to hide the X button in the header
 * @param {boolean} [props.scrolling] Caps the body height and scrolls its overflow
 * @param {boolean} [props.hideHeader] Removes the header row entirely
 * @param {'tiny'|'small'|'large'|'xlarge'} [props.size] Width preset forwarded to Modal, along with any other remaining props
 */
export const BasicModal = ({
  trigger,
  title,
  onClose,
  children,
  footer,
  show,
  showCloseButton = true,
  scrolling,
  hideHeader = false,
  ...otherProps
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const hasTrigger = isFunction(trigger)
  const resolvedOpen = hasTrigger ? isOpen : Boolean(show)

  const handleOpen = () => {
    setIsOpen(true)
  }

  const handleClose = () => {
    if (hasTrigger) {
      setIsOpen(false)
    }

    if (onClose) {
      onClose()
    }
  }

  return (
    <>
      {hasTrigger && trigger(handleOpen)}
      {resolvedOpen && (
        <Modal
          aria-label={isString(title) ? title : undefined}
          data-testid="basic-modal"
          onClose={handleClose}
          {...otherProps}
        >
          {!hideHeader && (
            <header className="modal-header">
              <h1>{title}</h1>
              {showCloseButton && (
                <Button
                  square
                  small
                  basic
                  onClick={handleClose}
                  data-testid="basic-modal-close-button"
                >
                  <IconX />
                </Button>
              )}
            </header>
          )}
          <div className={classNames('modal-body', { scrolling })}>
            {hasTrigger && isFunction(children) ? children(handleClose) : children}
          </div>
          {Boolean(footer) && <div className="modal-actions">{footer}</div>}
        </Modal>
      )}
    </>
  )
}
